import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
} from '@angular/core';
import { BzTableColumnManager, BzTableColumnSettings } from './bz-table-column-manager';

@Directive({
  selector: '[bzTableColumnManager]'
})
export class BzTableColumnManagerDirective implements AfterViewInit, OnDestroy {
  readonly uniqueId = input.required<string>({ alias: 'bzTableColumnManager' });
  readonly defaultWidths = input<Record<string, number>>({});
  readonly settings = input<BzTableColumnSettings>({ columnSizes: {}, columnOrder: [] });
  /** When false the header is left alone — no resize handles, no drag-reorder, no injected stylesheet. */
  readonly resizable = input(true);
  readonly settingsChange = output<BzTableColumnSettings>();

  private manager: BzTableColumnManager<BzTableColumnSettings> | null = null;
  private observer: MutationObserver | null = null;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    // Replaces the ngOnChanges this directive used before signal inputs. The guard matters:
    // the manager only exists after ngAfterViewInit, and this effect runs before that on the
    // first pass.
    effect(() => {
      const settings = this.settings();
      this.manager?.updateSettings(settings);
    });
  }

  ngAfterViewInit(): void {
    if (!this.resizable()) {
      return;
    }
    if (!this.uniqueId()) {
      console.warn('BzTableColumnManagerDirective: uniqueId is missing or empty.');
    }
    this.manager = new BzTableColumnManager(
      this.uniqueId(),
      this.defaultWidths(),
      this.settings(),
      (updatedSettings) => {
        this.settingsChange.emit(updatedSettings);
        this.cdr.detectChanges();
      },
      this.renderer,
    );
    this.manager.init(this.el.nativeElement);

    this.setupMutationObserver();
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.manager) {
      this.manager.destroy();
    }
  }

  private setupMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      let hasHeaderChanges = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check added nodes
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList?.contains('th') || element.classList?.contains('td') || element.querySelector?.('.th, .td')) {
                hasHeaderChanges = true;
                break;
              }
            }
          }
          if (hasHeaderChanges) break;

          // Check removed nodes
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i];
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList?.contains('th') || element.classList?.contains('td') || element.querySelector?.('.th, .td')) {
                hasHeaderChanges = true;
                break;
              }
            }
          }
          if (hasHeaderChanges) break;
        }
      }

      if (hasHeaderChanges && this.manager) {
        this.observer?.disconnect();

        this.manager.reinit(this.el.nativeElement);

        this.observer?.observe(this.el.nativeElement, {
          childList: true,
          subtree: true
        });
      }
    });

    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });
  }
}
